import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import BulkCreateAgencyOrderBundlesModal from '@modals/BulkCreateAgencyOrderBundlesModal'
import CreateOrEditOrderBundleItemModal from '@modals/CreateOrEditOrderBundleItemModal'
import DuplicateOrderBundleModal from '@modals/DuplicateOrderBundleModal'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import { OrderBundleModel } from '@models/orderBundle'
import { OrderBundleItemModel } from '@models/orderBundleItem'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditOrderBundleItemModal: false,
  showDuplicateOrderBundleModal: false,
  showBulkCreateAgencyOrderBundlesModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateAgencyOrderBundlesModal: {
      closeModal: () => setState({ showBulkCreateAgencyOrderBundlesModal: false }),
      bulkCreateAgencyOrderBundles: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditOrderBundleItemModal: {
      closeModal: () => setState({ showCreateOrEditOrderBundleItemModal: false }),
      createOrderBundleItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderBundleItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    DuplicateOrderBundleModal: {
      closeModal: () => setState({ showDuplicateOrderBundleModal: false }),
      duplicateOrderBundle: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const { orderBundleId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)

  const {
    showBulkCreateAgencyOrderBundlesModal,
    showCreateOrEditOrderBundleItemModal,
    showDuplicateOrderBundleModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAgencyOrderBundle: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteOrderBundleItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateAgencyOrderBundlesModal: (payload: object) => {
        setModalData('BulkCreateAgencyOrderBundlesModal', payload)
        setState({ showBulkCreateAgencyOrderBundlesModal: true })
      },
      showCreateOrEditOrderBundleItemModal: (payload: { orderBundleItem: Partial<OrderBundleItemModel> }) => {
        setModalData('CreateOrEditOrderBundleItemModal', payload)
        setState({ showCreateOrEditOrderBundleItemModal: true })
      },
      showDuplicateOrderBundleModal: (payload: { orderBundle: OrderBundleModel }) => {
        setModalData('DuplicateOrderBundleModal', payload)
        setState({ showDuplicateOrderBundleModal: true })
      },
      updateOrderBundle: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent orderBundleId={orderBundleId} />

        <BulkCreateAgencyOrderBundlesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkCreateAgencyOrderBundlesModal', setState)}
          showModal={showBulkCreateAgencyOrderBundlesModal}
        />

        <CreateOrEditOrderBundleItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditOrderBundleItemModal', setState)}
          showModal={showCreateOrEditOrderBundleItemModal}
        />

        <DuplicateOrderBundleModal
          // @ts-expect-error TS2322
          callbacks={callbacks('DuplicateOrderBundleModal', setState)}
          showModal={showDuplicateOrderBundleModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
